import React, { useEffect } from 'react';
import { Slide, ToastContainer } from 'react-toastify';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Layout } from './components/Layout';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SignalRService } from './utils/signalrService';
import { useSignalRSync } from './hooks/useSignalRSync';
import AppRoutes from './AppRoutes';
import './utils/errorHandler'; // Initialize global error handlers
import './custom.css';
import 'react-toastify/dist/ReactToastify.css';

// Create a client with our desired configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Let errors bubble to global handler
      retry: (failureCount, error: any) => {
        // Don't retry auth/validation errors
        if (error?.status === 401 || error?.status === 403) return false;
        return failureCount < 3;
      },
      // Trust SignalR for these
      refetchOnMount: false,      // SignalR tracks staleness
      refetchOnWindowFocus: false, // SignalR tracks staleness
      
      // But NOT for this - reconnection is uncertain
      refetchOnReconnect: true,   // Safety net for missed updates
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const AppWithSignalR: React.FC = () => {
  const { isAuthenticated, isInitialized } = useAuth();
  
  // Initialize centralized SignalR-Query sync
  useSignalRSync();

  // Initialize SignalR connection when user is authenticated
  useEffect(() => {
    const initializeSignalR = async () => {
      if (isAuthenticated && isInitialized) {
        // Initialize as soon as user is authenticated, don't wait for master key
        await SignalRService.initializeConnection();
      } else {
        // Close connection when user is not authenticated
        SignalRService.closeConnection();
      }
    };

    initializeSignalR();
  }, [isAuthenticated, isInitialized]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      SignalRService.closeConnection();
    };
  }, []);

  return (
    <Layout>
      <AppRoutes />
      
      {/* Toast container for global notifications */}
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        transition={Slide}
        closeOnClick={false}
        style={{width: "350px", zIndex: 100001}}
        toastStyle={{fontSize: "100%"}}
        draggablePercent={50}
        className="custom-toast-container"
      />
    </Layout>
  );
};

export default function App(): React.ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <AppWithSignalR />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}